import { ReactNode } from 'react';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import Grow from '@mui/material/Grow';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent, { DialogContentProps } from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import BaseButton from './BaseButton';
import { Check, Close } from '@mui/icons-material';
import '@/styles/components/ui/BaseDialog.scss';
import { CircularProgress, FadeProps, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface BaseDialogProps extends Omit<DialogProps, 'open'> {
    open: boolean;
    children: ReactNode;
    dialogTitle: string;
    confirmButtonText?: ReactNode;
    cancelButtonText?: string;
    onConfirm?: () => void;
    onClose?: () => void;
    disableConfirm?: boolean;
    showTopRightClose?: boolean;
    dialogContentProps?: DialogContentProps;
    showSpinnerOnDisable?: boolean;
    TransitionProps?: Partial<FadeProps>;
}

function BaseDialog({
    open,
    children,
    dialogTitle,
    confirmButtonText,
    cancelButtonText,
    onConfirm,
    onClose,
    disableConfirm = false,
    showTopRightClose = false,
    dialogContentProps,
    showSpinnerOnDisable = true,
    TransitionProps,
    ...props
}: BaseDialogProps) {
    const { t } = useTranslation('common');

    const handleConfirm = () => {
        onConfirm?.();
    };
    const handleClose = () => {
        onClose?.();
    };

    const contentSx = (dialogContentProps?.sx ?? {}) as {
        display?: React.CSSProperties['display'];
        flexDirection?: React.CSSProperties['flexDirection'];
        alignItems?: React.CSSProperties['alignItems'];
        [key: string]: any;
    };

    const cancelLabel = cancelButtonText ?? t('Close');

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="sm"
            TransitionComponent={Grow}
            TransitionProps={TransitionProps}
            disableEnforceFocus={false}
            disableAutoFocus={false}
            onClose={(_, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    return;
                }
                handleClose();
            }}
            disableScrollLock={true}
            {...props}
        >
            <div className="base-dialog">
                {showTopRightClose && (
                    <IconButton
                        onClick={handleClose}
                        aria-label={t('Close')}
                        style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            zIndex: 10,
                            color: '#555',
                        }}
                    >
                        <Close />
                    </IconButton>
                )}
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent
                    className="dialog-content"
                    {...dialogContentProps}
                    sx={{
                        pb: 1,
                        minHeight: 'auto',
                        display: contentSx.display ?? 'flex',
                        flexDirection: contentSx.flexDirection,
                        alignItems: contentSx.alignItems,
                        ...contentSx,
                    }}
                >
                    {children}
                </DialogContent>
                <DialogActions className="dialog-actions">
                    {confirmButtonText && (
                        <BaseButton
                            startIcon={<Check />}
                            className="confirm-button"
                            color="success"
                            variant="contained"
                            onClick={handleConfirm}
                            disabled={disableConfirm}
                        >
                            {disableConfirm && showSpinnerOnDisable ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                confirmButtonText
                            )}
                        </BaseButton>
                    )}
                    {cancelLabel && (
                        <BaseButton
                            startIcon={<Close />}
                            variant="outlined"
                            className="cancel-button"
                            onClick={handleClose}
                            autoFocus
                        >
                            {cancelLabel}
                        </BaseButton>
                    )}
                </DialogActions>
            </div>
        </Dialog>
    );
}

export default BaseDialog;
